'use client';

/**
 * NavigationProgressContext
 *
 * GitHub-style top-of-page progress bar. Triggered via `startProgress()` when
 * a nav click fires, auto-completes when `pathKey` changes. Router-agnostic —
 * portals pass the current route key (typically `pathname`) in.
 *
 * The provider stores state only. The bar itself is rendered by
 * `<NavigationProgressBar />`, which is mounted inside `PortalLayoutBody` so
 * it can read `useSidebarWidth()` and align with the content area's top
 * border (instead of spanning the full viewport).
 */

import { alpha, Box } from '@mui/material';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { HEADER_HEIGHT } from '../components/AppNavBar/AppNavBar';
import { useSidebarWidth } from './SidebarWidthContext';

// Bar height — thin hairline so it reads as part of the header's bottom border.
const BAR_HEIGHT = 2;

interface NavigationProgressContextType {
  startProgress: () => void;
  completeProgress: () => void;
  isNavigating: boolean;
  progress: number;
}

const NavigationProgressContext = createContext<NavigationProgressContextType | null>(null);

export function useNavigationProgress(): NavigationProgressContextType {
  const context = useContext(NavigationProgressContext);
  if (!context) {
    throw new Error('useNavigationProgress must be used within NavigationProgressProvider');
  }
  return context;
}

interface NavigationProgressProviderProps {
  children: React.ReactNode;
  /** Route key (typically pathname, optionally + search). Progress auto-completes when it changes. */
  pathKey: string;
}

export function NavigationProgressProvider({ children, pathKey }: NavigationProgressProviderProps) {
  const [isNavigating, setIsNavigating] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousPathRef = useRef(pathKey);

  const startProgress = useCallback(() => {
    setIsNavigating(true);
    setProgress(0);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    let currentProgress = 0;
    intervalRef.current = setInterval(() => {
      currentProgress += Math.random() * 10 + 5;
      if (currentProgress > 85) {
        currentProgress = 85;
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
      setProgress(currentProgress);
    }, 80);
  }, []);

  const completeProgress = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setProgress(100);

    timeoutRef.current = setTimeout(() => {
      setIsNavigating(false);
      setProgress(0);
      timeoutRef.current = null;
    }, 200);
  }, []);

  // Auto-complete on route change
  useEffect(() => {
    if (pathKey !== previousPathRef.current && isNavigating) {
      completeProgress();
    }
    previousPathRef.current = pathKey;
  }, [pathKey, isNavigating, completeProgress]);

  // Cleanup both timers on unmount so a pending completion timeout can't
  // fire setState after the provider has torn down (shell unmount, etc).
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <NavigationProgressContext.Provider value={{ startProgress, completeProgress, isNavigating, progress }}>
      {children}
    </NavigationProgressContext.Provider>
  );
}

/**
 * Renders the progress bar aligned with AppNavBar's bottom border, scoped to
 * the content area only (starts after the sidebar rail + panel). Must be
 * mounted inside both NavigationProgressProvider and SidebarWidthProvider —
 * PortalLayout does this automatically.
 */
export function NavigationProgressBar() {
  const { isNavigating, progress } = useNavigationProgress();
  const { sidebarWidth } = useSidebarWidth();

  if (!isNavigating) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        // Flush with the content area's top border (AppNavBar bottom edge)
        top: HEADER_HEIGHT - BAR_HEIGHT,
        // Start where the sidebar ends on desktop; span full width on mobile
        // (mobile drawer is temporary, not inline)
        left: { xs: 0, md: `${sidebarWidth}px` },
        right: 0,
        height: BAR_HEIGHT,
        zIndex: (theme) => theme.zIndex.tooltip + 1,
        backgroundColor: 'transparent',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={(theme) => ({
          height: '100%',
          backgroundColor: 'primary.main',
          backgroundImage: `linear-gradient(90deg, transparent, ${alpha(theme.palette.common.white, 0.4)}, transparent)`,
          backgroundSize: '200% 100%',
          animation: progress < 100 ? 'navProgressShimmer 1.5s infinite' : 'none',
          width: `${progress}%`,
          transition: 'width 0.15s ease-out',
          boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}, 0 0 4px ${alpha(theme.palette.primary.main, 0.3)}`,
          '@keyframes navProgressShimmer': {
            '0%': { backgroundPosition: '-200% 0' },
            '100%': { backgroundPosition: '200% 0' },
          },
        })}
      />
    </Box>
  );
}
